import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, MessageBarBody, MessageBarTitle, Spinner } from '@fluentui/react-components';
import { Add16Regular, Delete16Regular, Edit16Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { ErrorType } from '../../../models/HttpError';

// guía
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideTooltip } from '../../../theme/components/GuideTooltip';

import './PersonContactList.Styles.css';

export interface PersonContactListProps<T extends { id: number }> {
  /** Título del grupo ("Teléfonos") y su ícono. */
  title: string;
  icon: React.ReactNode;
  items: T[];
  /** Valor principal de la fila (número, correo, calle). */
  primary: (item: T) => React.ReactNode;
  /** Línea secundaria opcional (comentario, ciudad y país). */
  secondary?: (item: T) => React.ReactNode;
  /** Texto del ítem para el diálogo de borrado y las etiquetas accesibles. */
  itemLabel: (item: T) => string;
  isLoading?: boolean;
  failOnLoading?: boolean;
  isDeleting?: boolean;
  failOnDeleting?: boolean;
  error?: ErrorType;
  addLabel: string;
  emptyText: string;
  loadingLabel: string;
  loadErrorTitle: string;
  deleteTitle: string;
  deleteText: (item: T) => string;
  onAdd: () => void;
  onEdit: (item: T) => void;
  onDelete: (item: T) => void;
  onRetry: () => void;
  testId?: string;
  /** Ocupa todo el ancho de la tarjeta (domicilios). */
  wide?: boolean;
}

/**
 * Grupo compacto de datos de contacto (teléfonos, correos, domicilios), al estilo de la tarjeta
 * de contacto de Outlook pero con los tokens de la guía: encabezado con ícono, título en
 * mayúsculas y contador, filas de valor con acciones al pasar el mouse y "Agregar" al pie.
 * Los formularios los aporta cada lista concreta.
 */
export const PersonContactList = <T extends { id: number }>(props: PersonContactListProps<T>) => {
  const [t] = useTranslation();
  const [deleting, setDeleting] = useState<T | undefined>(undefined);
  const [deleteRequested, setDeleteRequested] = useState(false);
  const testId = props.testId ?? 'pcl';

  // El diálogo de borrado se cierra solo cuando la operación terminó bien.
  useEffect(() => {
    if (deleteRequested && !props.isDeleting && !props.failOnDeleting) {
      setDeleting(undefined);
      setDeleteRequested(false);
    }
  }, [deleteRequested, props.isDeleting, props.failOnDeleting]);

  const editLabel = t('persons.common.edit', { defaultValue: 'Editar' });
  const deleteLabel = t('persons.common.delete', { defaultValue: 'Eliminar' });

  const renderBody = () => {
    if (props.isLoading && props.items.length === 0) {
      return <div className="pcl-group__state" role="status" aria-live="polite" data-testid={`${testId}-loading`}><Spinner size="extra-tiny" /> {props.loadingLabel}</div>;
    }
    if (props.failOnLoading && props.items.length === 0) return null;
    if (props.items.length === 0) {
      return <div className="pcl-group__state pcl-group__state--empty" data-testid={`${testId}-empty`}>{props.emptyText}</div>;
    }
    return (
      <ul className="pcl-group__list" data-testid={`${testId}-list`}>
        {props.items.map((item) => (
          <li key={item.id} className="pcl-row" data-testid={`${testId}-row-${item.id}`}>
            <div className="pcl-row__text">
              <span className="pcl-row__value">{props.primary(item)}</span>
              {props.secondary && <span className="pcl-row__sub">{props.secondary(item)}</span>}
            </div>
            <span className="pcl-row__actions">
              <GuideTooltip content={editLabel} relationship="label">
                <Button appearance="subtle" size="small" icon={<Edit16Regular />} aria-label={`${editLabel} ${props.itemLabel(item)}`} disabled={props.isDeleting} onClick={() => props.onEdit(item)} data-testid={`${testId}-edit-${item.id}`} />
              </GuideTooltip>
              <GuideTooltip content={deleteLabel} relationship="label">
                <Button appearance="subtle" size="small" className="pcl-row__delete" icon={<Delete16Regular />} aria-label={`${deleteLabel} ${props.itemLabel(item)}`} disabled={props.isDeleting} onClick={() => { setDeleteRequested(false); setDeleting(item); }} data-testid={`${testId}-delete-${item.id}`} />
              </GuideTooltip>
            </span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <section className={`pcl-group${props.wide ? ' pcl-group--wide' : ''}`} aria-label={props.title} data-testid={testId}>
      <header className="pcl-group__head">
        <span className="pcl-group__icon" aria-hidden="true">{props.icon}</span>
        <span className="pcl-group__title">{props.title}</span>
        <span className="pcl-group__count" aria-label={`${props.items.length}`}>{props.items.length}</span>
        {props.isLoading && props.items.length > 0 && <Spinner size="extra-tiny" aria-label={props.loadingLabel} />}
      </header>

      {props.failOnLoading && (
        <GuideMessageBar intent="error" layout="multiline" data-testid={`${testId}-error`}>
          <MessageBarBody>
            <MessageBarTitle>{props.loadErrorTitle}</MessageBarTitle>
            {props.error?.ErrorMessage}
          </MessageBarBody>
          <div className="pcl-retry"><Button size="small" onClick={props.onRetry}>{t('persons.common.retry', { defaultValue: 'Reintentar' })}</Button></div>
        </GuideMessageBar>
      )}

      {renderBody()}

      <footer className="pcl-group__foot">
        <Button appearance="transparent" size="small" className="pcl-group__add" icon={<Add16Regular />} onClick={props.onAdd} data-testid={`${testId}-add`}>
          {props.addLabel}
        </Button>
      </footer>

      <Dialog open={!!deleting} modalType="alert" onOpenChange={(_event, data) => { if (!data.open && !props.isDeleting) { setDeleting(undefined); setDeleteRequested(false); } }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{props.deleteTitle}</DialogTitle>
            <DialogContent>
              <p className="pcl-dialog__text">{deleting ? props.deleteText(deleting) : ''}</p>
              {deleteRequested && props.failOnDeleting && (
                <GuideMessageBar intent="error" layout="multiline">
                  <MessageBarBody>
                    <MessageBarTitle>{t('persons.common.deleteFailed', { defaultValue: 'No se pudo eliminar.' })}</MessageBarTitle>
                    {props.error?.ErrorMessage}
                  </MessageBarBody>
                </GuideMessageBar>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => { setDeleting(undefined); setDeleteRequested(false); }} disabled={props.isDeleting}>
                {t('persons.common.cancel', { defaultValue: 'Cancelar' })}
              </Button>
              <Button
                appearance="primary"
                className="pcl-danger"
                disabled={props.isDeleting}
                icon={props.isDeleting ? <Spinner size="extra-tiny" /> : undefined}
                onClick={() => { if (deleting) { setDeleteRequested(true); props.onDelete(deleting); } }}
                data-testid={`${testId}-delete-confirm`}
              >
                {deleteLabel}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </section>
  );
};

/** Tarjeta que agrupa varios grupos de contacto (como la tarjeta de contacto de Outlook). */
export const PersonContactCard = ({ children, className, ...rest }: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...rest} className={`pcl-card${className ? ` ${className}` : ''}`}>
    {children}
  </div>
);

export default PersonContactList;
